import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Images, ZoomIn } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = {
  all: { nl: 'Alle Foto\'s', en: 'All Photos', es: 'Todas' },
  general: { nl: 'Algemeen', en: 'General', es: 'General' },
  grass: { nl: 'Gras Maaien', en: 'Grass Cutting', es: 'Corte de Césped' },
  street: { nl: 'Straatvegen', en: 'Street Cleaning', es: 'Limpieza de Calles' },
  garage: { nl: 'Garage', en: 'Garage', es: 'Garaje' },
  window: { nl: 'Ramen', en: 'Windows', es: 'Ventanas' },
  electrical: { nl: 'Elektra', en: 'Electrical', es: 'Eléctrico' },
  renovation: { nl: 'Renovatie', en: 'Renovation', es: 'Renovación' },
};

export default function Gallery() {
  const { lang } = useLanguage();
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    document.title = 'Galerij | Orange Onderhoudsbedrijf';
    axios.get(`${API}/gallery`).then(r => setPhotos(r.data)).catch(() => {});
  }, []);

  const filtered = filter === 'all' ? photos : photos.filter(p => p.category === filter);

  const titles = { nl: 'Galerij', en: 'Gallery', es: 'Galería' };
  const subtitles = {
    nl: 'Bekijk ons werk in foto\'s',
    en: 'Browse our work in photos',
    es: 'Vea nuestro trabajo en fotos'
  };
  const emptyText = {
    nl: 'Nog geen foto\'s toegevoegd.',
    en: 'No photos added yet.',
    es: 'Aún no hay fotos añadidas.'
  };

  return (
    <div className="bg-[#F8FAFC] pt-20">
      {/* Header */}
      <section data-testid="gallery-header" className="bg-white border-b border-slate-100 px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5F00]">Orange Onderhoudsbedrijf</span>
          <h1 className="text-5xl md:text-6xl font-black text-[#0F172A] mt-3 mb-5">{titles[lang]}</h1>
          <p className="text-lg text-[#64748B]">{subtitles[lang]}</p>
        </div>
      </section>

      {/* Filter tabs */}
      {photos.length > 0 && (
        <div className="px-6 md:px-12 lg:px-24 pt-10">
          <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
            {Object.entries(CATEGORIES).map(([key, labels]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                data-testid={`filter-${key}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  filter === key
                    ? 'bg-[#FF5F00] text-white shadow-sm'
                    : 'bg-white border border-slate-200 text-[#64748B] hover:border-[#FF5F00] hover:text-[#FF5F00]'
                }`}
              >
                {labels[lang]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Grid */}
      <section className="px-6 md:px-12 lg:px-24 py-10 pb-20">
        {filtered.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Images size={28} className="text-slate-300" />
            </div>
            <p className="text-[#64748B]">{emptyText[lang]}</p>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
            {filtered.map(photo => (
              <div
                key={photo.id}
                data-testid={`gallery-photo-${photo.id}`}
                onClick={() => setSelected(photo)}
                className="group break-inside-avoid bg-white border border-slate-100 rounded-2xl overflow-hidden cursor-pointer hover:border-orange-200 hover:shadow-lg transition-all"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={photo.image_url}
                    alt={photo.title || ''}
                    className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=60'; }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                    <ZoomIn size={28} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                {(photo.title || photo.caption) && (
                  <div className="p-4">
                    {photo.title && <p className="font-semibold text-[#0F172A] text-sm">{photo.title}</p>}
                    {photo.caption && <p className="text-[#64748B] text-xs mt-1">{photo.caption}</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox */}
      {selected && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <button
            onClick={() => setSelected(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all"
          >
            <X size={20} />
          </button>
          <div className="max-w-4xl w-full" onClick={e => e.stopPropagation()}>
            <img
              src={selected.image_url}
              alt={selected.title || ''}
              className="w-full max-h-[80vh] object-contain rounded-xl"
              onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=60'; }}
            />
            {(selected.title || selected.caption) && (
              <div className="mt-4 text-center">
                {selected.title && <p className="font-bold text-white text-lg">{selected.title}</p>}
                {selected.caption && <p className="text-slate-400 text-sm mt-1">{selected.caption}</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
